<template>
  <div>
    <v-card>
      <v-card-title class="d-flex align-center flex-wrap">
        <v-btn
          icon
          :ripple="false"
          @click="moveMonth(-1)"
        >
          <v-icon>{{ icons.mdiChevronLeft }}</v-icon>
        </v-btn>
        <v-btn
          icon
          :ripple="false"
          @click="moveMonth(1)"
        >
          <v-icon>{{ icons.mdiChevronRight }}</v-icon>
        </v-btn>
        <span class="text-h6 me-3">{{ monthTitle }}</span>
        <v-btn
          small
          outlined
          :ripple="false"
          @click="goToday"
        >
          今日
        </v-btn>
        <v-btn
          v-if="canManage"
          small
          color="primary"
          class="ms-3"
          :ripple="false"
          @click="isAddDialogOpen = true"
        >
          <v-icon
            left
            size="16"
          >
            {{ icons.mdiPlus }}
          </v-icon>
          シフト登録
        </v-btn>
        <v-btn
          v-if="canManage"
          icon
          :ripple="false"
          title="必要人数の設定"
          @click="isStaffingDialogOpen = true"
        >
          <v-icon>{{ icons.mdiCogOutline }}</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <span class="month-summary">
          <span
            class="month-summary__item"
            :class="{ 'month-summary__item--warn': monthSummary.shortDays > 0 }"
          >不足 {{ monthSummary.shortDays }}日</span>
          <span class="month-summary__item">延べ出勤 {{ monthSummary.totalScheduled }}人</span>
          <span class="month-summary__item">予約 {{ monthSummary.totalReservations }}件</span>
        </span>
      </v-card-title>

      <v-card-text
        v-if="isLoading"
        class="text-center py-12 loading-muted"
      >
        読み込み中...
      </v-card-text>
      <v-card-text
        v-else
        class="pt-0"
      >
        <div class="month-grid month-grid--header">
          <div
            v-for="w in weekdayLabels"
            :key="`wd-${w}`"
            class="month-grid__weekday"
          >
            {{ w }}
          </div>
        </div>
        <div class="month-grid">
          <div
            v-for="(cell, i) in calendarCells"
            :key="`cell-${i}`"
            class="month-cell"
            :class="{
              'month-cell--blank': !cell,
              'month-cell--short': cell && cell.isShort,
              'month-cell--today': cell && cell.isToday,
            }"
            :title="cell ? 'タップでこの日の日別予約（予約ボード・出勤）を開く' : null"
            @click="cell && $emit('open-day', cell.date)"
          >
            <template v-if="cell">
              <div class="month-cell__num">
                {{ cell.dayNum }}
              </div>
              <div
                v-if="cell.required != null || cell.scheduledCount > 0"
                class="month-cell__row month-cell__row--scheduled"
                :class="{ 'month-cell__row--warn': cell.isShort }"
              >
                出勤 {{ cell.scheduledCount }}<template v-if="cell.required != null">
                  /{{ cell.required }}
                </template>
              </div>
              <div
                v-if="cell.reservationCount > 0"
                class="month-cell__row month-cell__row--reservation"
              >
                予約 {{ cell.reservationCount }}件
              </div>
              <div
                v-if="cell.statusCount > 0"
                class="month-cell__row month-cell__row--status"
              >
                遅欠 {{ cell.statusCount }}
              </div>
            </template>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- シフト登録（複数人同時登録可・日付選択式） -->
    <v-dialog
      v-model="isAddDialogOpen"
      width="480"
    >
      <v-card>
        <v-card-title>シフト登録</v-card-title>
        <v-card-text class="pb-6">
          <shift-bulk-add-form
            v-if="isAddDialogOpen"
            :casts="casts"
            @created="onBulkAdded"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 必要人数設定 -->
    <staffing-target-dialog
      v-model="isStaffingDialogOpen"
      :targets="staffingTargets"
      @saved="fetchMonth"
    />
  </div>
</template>

<script>
import {
  ref, computed, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiChevronLeft, mdiChevronRight, mdiPlus, mdiCogOutline,
} from '@mdi/js'
import { format } from 'date-fns'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import StaffingTargetApi from '@/api/waiter/StaffingTarget'
import ReservationApi from '@/api/waiter/Reservation'
import TimecardApi from '@/api/waiter/Timecard'
import UserApi from '@/api/waiter/User'
import useCurrentData from '@/views/composable/useCurrentData'
import { getBusinessTodayStr } from '@/views/waiter/floorUtils'
import {
  resolveRequiredCount,
  countApprovedByDate,
  buildStaffingSummary,
  collectTimecardStatusesByDate,
  groupReservationsByDate,
  buildMonthSummary,
} from '@/views/waiter/shiftStaffingUtils'
import ShiftBulkAddForm from '@/views/waiter/settings/ShiftBulkAddForm.vue'
import StaffingTargetDialog from '@/views/waiter/settings/StaffingTargetDialog.vue'

export default {
  components: {
    ShiftBulkAddForm,
    StaffingTargetDialog,
  },
  setup(_, { emit }) {
    const vm = getCurrentInstance().proxy
    const { currentUser, currentClub } = useCurrentData()

    const weekdayLabels = ['日', '月', '火', '水', '木', '金', '土']
    const currentMonth = ref(new Date())
    const isLoading = ref(false)

    // ── 月データ ──
    const schedules = ref([])
    const staffingTargets = ref([])
    const reservationsByDate = ref({})
    const timecardStatusesByDate = ref({})
    const casts = ref([])

    const canManage = computed(() => {
      if (['waiter', 'admin'].includes(currentUser.value?.role)) return true

      return vm.$can('read', 'custom:settings_work_schedule_manage')
    })

    const businessTodayStr = computed(() => getBusinessTodayStr(new Date(), currentClub.value?.openAt))

    const monthStartStr = computed(() => format(new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth(), 1), 'yyyy-MM-dd'))
    const monthEndStr = computed(() => format(new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 0), 'yyyy-MM-dd'))

    const monthDates = computed(() => {
      const daysInMonth = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 0).getDate()

      return Array.from({ length: daysInMonth }, (unused, i) => format(new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth(), i + 1), 'yyyy-MM-dd'))
    })

    const fetchMonth = async () => {
      isLoading.value = true
      try {
        const [schedulesRes, targetsRes, reservationsRes, timecardsRes] = await Promise.all([
          WorkScheduleApi.getWorkSchedules({
            status: 'approved', requestType: 'work', startDate: monthStartStr.value, endDate: monthEndStr.value,
          }),
          StaffingTargetApi.getStaffingTargets(),
          // 予約・勤怠は取得できない場合（権限・未導入等）は非表示で degrade。件数表示のみのため詳細は不要
          ReservationApi.getReservations({ from: monthStartStr.value, to: monthEndStr.value }).catch(() => null),
          monthStartStr.value <= businessTodayStr.value
            ? TimecardApi.getTimecards({
              date: monthStartStr.value,
              endDate: monthEndStr.value < businessTodayStr.value ? monthEndStr.value : businessTodayStr.value,
            }).catch(() => null)
            : Promise.resolve(null),
        ])

        if (schedulesRes?.data) {
          schedules.value = schedulesRes.data.work_schedules.data.map(d => ({ id: d.id, ...d.attributes }))
        }
        if (targetsRes?.data) {
          staffingTargets.value = targetsRes.data.staffing_targets.data.map(d => ({
            id: Number(d.id),
            dayOfWeek: d.attributes.dayOfWeek,
            date: d.attributes.date ? String(d.attributes.date).slice(0, 10) : null,
            requiredCount: d.attributes.requiredCount,
          }))
        }
        const reservationRows = reservationsRes?.data ? reservationsRes.data.reservations.data : []
        reservationsByDate.value = groupReservationsByDate(reservationRows)
        timecardStatusesByDate.value = timecardsRes?.data
          ? collectTimecardStatusesByDate(timecardsRes.data.timecards.data)
          : {}
      } finally {
        isLoading.value = false
      }
    }

    const getCasts = async () => {
      const res = await UserApi.getUsers(['cast'])
      if (res?.data) {
        casts.value = res.data.users.data.map(u => ({ id: Number(u.id), name: u.attributes?.name || `ID:${u.id}` }))
      }
    }

    // ── 集計 ──
    const approvedByDate = computed(() => countApprovedByDate(schedules.value))
    const staffingRows = computed(() => buildStaffingSummary(staffingTargets.value, approvedByDate.value, monthDates.value))
    const monthSummary = computed(() => buildMonthSummary(staffingRows.value, approvedByDate.value, reservationsByDate.value, monthDates.value))

    const monthTitle = computed(() => `${currentMonth.value.getFullYear()}年 ${currentMonth.value.getMonth() + 1}月`)

    const calendarCells = computed(() => {
      const year = currentMonth.value.getFullYear()
      const month = currentMonth.value.getMonth()
      const firstWday = new Date(year, month, 1).getDay()
      const todayStr = format(new Date(), 'yyyy-MM-dd')

      const cells = Array.from({ length: firstWday }, () => null)
      monthDates.value.forEach(dateStr => {
        const required = resolveRequiredCount(staffingTargets.value, dateStr)
        const scheduledCount = approvedByDate.value[dateStr] || 0
        cells.push({
          date: dateStr,
          dayNum: parseInt(dateStr.slice(8, 10), 10),
          required,
          scheduledCount,
          isShort: required != null && scheduledCount < required,
          reservationCount: (reservationsByDate.value[dateStr] || []).length,
          statusCount: (timecardStatusesByDate.value[dateStr] || []).length,
          isToday: dateStr === todayStr,
        })
      })
      while (cells.length % 7 !== 0) cells.push(null)

      return cells
    })

    const moveMonth = diff => {
      const d = new Date(currentMonth.value)
      d.setDate(1)
      d.setMonth(d.getMonth() + diff)
      currentMonth.value = d
      fetchMonth()
    }

    const goToday = () => {
      currentMonth.value = new Date()
      fetchMonth()
    }

    // ── ダイアログ ──
    const isAddDialogOpen = ref(false)

    const onBulkAdded = async () => {
      isAddDialogOpen.value = false
      emit('changed')
      await fetchMonth()
    }

    const isStaffingDialogOpen = ref(false)

    fetchMonth()
    getCasts()

    return {
      weekdayLabels,
      isLoading,
      monthTitle,
      monthSummary,
      calendarCells,
      canManage,
      casts,
      staffingTargets,
      moveMonth,
      goToday,
      fetchMonth,
      isAddDialogOpen,
      onBulkAdded,
      isStaffingDialogOpen,
      icons: {
        mdiChevronLeft,
        mdiChevronRight,
        mdiPlus,
        mdiCogOutline,
      },
    }
  },
}
</script>

<style scoped>
.loading-muted {
  color: #94a3b8;
  font-weight: 500;
}

.month-summary {
  display: flex;
  gap: 16px;
  font-size: 15.5px;
}

.month-summary__item--warn {
  color: #ef4444;
  font-weight: 700;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.month-grid--header {
  margin-bottom: 4px;
}

.month-grid__weekday {
  text-align: center;
  font-size: 14.5px;
  font-weight: 700;
  color: #94a3b8;
  padding: 4px 0;
}

.month-cell {
  min-height: 88px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
}

.month-cell--blank {
  border: none;
  cursor: default;
}

.month-cell--short {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.35);
}

.month-cell--today {
  border-color: #a60df2;
  border-width: 2px;
}

.month-cell__num {
  font-size: 15.5px;
  font-weight: 700;
  margin-bottom: 4px;
}

.month-cell__row {
  font-size: 13.5px;
  line-height: 1.6;
  white-space: nowrap;
}

.month-cell__row--warn {
  color: #ef4444;
  font-weight: 700;
}

.month-cell__row--reservation {
  color: #22d3ee;
}

.month-cell__row--status {
  color: #f59e0b;
}
</style>
